/**
 * UserAvatar - user profile avatar with a load-aware pulse.
 *
 * User profile photos are network-fetched (signed S3 URLs), so they arrive a
 * beat after the surrounding chrome renders. This shows an animated pulse in
 * that gap instead of flashing the initials, then falls back to the initials
 * only if there is no image or it genuinely fails to load. Mirrors the
 * behaviour baked into AgentIcon for agent avatars.
 */

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"
import { useState } from "react"

interface UserAvatarProps {
  /** Profile image URL (signed). Null/empty falls straight back to initials. */
  image?: string | null
  /** Display name, used for the image alt text. */
  name?: string | null
  /** Pre-computed initials shown as the fallback. */
  initials: string
  /** Classes for the Avatar root (sizing, etc.). */
  className?: string
  /** Classes for the fallback (colours, text size) when showing initials. */
  fallbackClassName?: string
  /** Border-radius class applied throughout. Defaults to rounded-lg. */
  rounded?: string
}

export function UserAvatar({
  image,
  name,
  initials,
  className,
  fallbackClassName,
  rounded = "rounded-lg",
}: UserAvatarProps) {
  const [status, setStatus] = useState<"idle" | "loading" | "loaded" | "error">(
    "idle",
  )
  // Pulse only while a real image is in flight; no image or a load error shows
  // the initials immediately (no perpetual shimmer on initials-only avatars).
  const showPulse = !!image && status !== "loaded" && status !== "error"

  return (
    <Avatar className={cn(rounded, className)}>
      {image ? (
        <AvatarImage
          src={image}
          alt={name ?? ""}
          className={rounded}
          onLoadingStatusChange={setStatus}
        />
      ) : null}
      <AvatarFallback
        className={cn(rounded, showPulse ? "bg-transparent" : fallbackClassName)}
      >
        {showPulse ? (
          <span
            className={cn("block h-full w-full bg-muted animate-pulse", rounded)}
            aria-label="Loading avatar"
          />
        ) : (
          initials
        )}
      </AvatarFallback>
    </Avatar>
  )
}
